import React, { useState, useEffect, useRef } from 'react';
import {
  Phone,
  MapPin,
  Star,
  CheckCircle,
  X,
  ArrowRight,
  Home,
  Building,
  Droplets,
  Share2,
  Mail,
  MessageSquare,
  Facebook,
  Send,
  User,
  Menu
} from 'lucide-react';
import { MapContainer, TileLayer, Circle, Marker, Popup } from 'react-leaflet';
import LazyLoad from 'react-lazyload';
import 'leaflet/dist/leaflet.css';
import './App.css';

// Add this utility function to parse URL parameters
const getUrlParameter = (name) => {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) || '';
};

const CeponisGutters = () => {
  const [estimateStep, setEstimateStep] = useState(-1);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    zipcode: '',
    address: '',
    service: '',
    reminders: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = useRef(null);
  const hoverTimeout = useRef(null);
  const servicesRef = useRef(null);
  const serviceAreaRef = useRef(null);
  const contactRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const structuredData = [
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Ceponis Gutters",
        "description": "Affordable gutter cleaning, roof cleaning, and roof algae removal services in Naperville, IL and within a 20-mile radius. Get a free estimate from trusted local professionals. Call (630) 780-8936!",
        "url": "https://ceponisgutters.com",
        "telephone": "(630) 780-8936",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Serving Naperville and surrounding areas",
          "addressLocality": "Naperville",
          "addressRegion": "IL",
          "postalCode": Object.keys(validZipcodes).join(", "),
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 41.7508,
          "longitude": -88.1535
        },
        "areaServed": [
          {
            "@type": "GeoCircle",
            "geoMidpoint": {
              "@type": "GeoCoordinates",
              "latitude": 41.7508,
              "longitude": -88.1535
            },
            "geoRadius": 32186.9
          },
          {
            "@type": "Place",
            "name": "Naperville, IL",
            "containedInPlace": {
              "@type": "State",
              "name": "Illinois"
            }
          },
          ...Object.values(validZipcodes).map(town => ({
            "@type": "Place",
            "name": town
          }))
        ],
        "serviceType": ["Gutter Cleaning", "Roof Cleaning", "Roof Algae Removal"],
        "priceRange": "$$",
        "openingHours": "Mo,Tu,We,Th,Fr,Sa 08:00-18:00",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "50"
        },
        "sameAs": [
          "https://nextdoor.com/pages/ceponis-gutters-naperville-il/"
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "url": "https://ceponisgutters.com",
        "name": "Ceponis Gutters",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://ceponisgutters.com/?service={search_term_string}",
          "query-input": "required name=search_term_string"
        },
        "hasPart": [
          {
            "@type": "WebPage",
            "url": "https://ceponisgutters.com/?service=gutter-cleaning",
            "name": "Gutter Cleaning",
            "description": "Professional gutter cleaning services in Naperville, IL to prevent water damage and maintain your home."
          },
          {
            "@type": "WebPage",
            "url": "https://ceponisgutters.com/?section=contact",
            "name": "Contact Us",
            "description": "Contact Ceponis Gutters for expert gutter and roof cleaning services in Naperville, IL."
          },
          {
            "@type": "WebPage",
            "url": "https://ceponisgutters.com/?service=gutter-cleaning",
            "name": "Get Estimate",
            "description": "Get a free, no-obligation estimate for gutter cleaning, roof cleaning, or algae removal in Naperville, IL."
          }
        ]
      }
    ];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'structured-data';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    const urlParams = new URLSearchParams(window.location.search);
    const serviceParam = urlParams.get('service');

    // Meta tag references for cleanup
    let metaDesc, metaOgTitle, metaOgDesc;

    if (serviceParam) {
      const serviceMap = {
        'gutter-cleaning': 'Gutter Cleaning',
        'roof-cleaning': 'Roof Cleaning',
        'roof-algae-removal': 'Roof Algae Removal'
      };

      const serviceTitle = serviceMap[serviceParam] || 'Gutter & Roof Cleaning';
      const fullTitle = `Ceponis Gutters - Affordable ${serviceTitle} in Naperville, IL`;
      const description = `Affordable ${serviceTitle.toLowerCase()} in Naperville, IL and within a 20-mile radius. Get a free estimate from trusted local professionals. Call (630) 780-8936!`;

      document.title = fullTitle;

      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      metaDesc.content = description;
      metaDesc.id = 'meta-desc';
      document.head.appendChild(metaDesc);

      metaOgTitle = document.createElement('meta');
      metaOgTitle.setAttribute('property', 'og:title');
      metaOgTitle.content = fullTitle;
      metaOgTitle.id = 'meta-og-title';
      document.head.appendChild(metaOgTitle);

      metaOgDesc = document.createElement('meta');
      metaOgDesc.setAttribute('property', 'og:description');
      metaOgDesc.content = description;
      metaOgDesc.id = 'meta-og-desc';
      document.head.appendChild(metaOgDesc);
    }

    return () => {
      const oldScript = document.getElementById('structured-data');
      if (oldScript) document.head.removeChild(oldScript);

      ['meta-desc', 'meta-og-title', 'meta-og-desc'].forEach(id => {
        const el = document.getElementById(id);
        if (el) document.head.removeChild(el);
      });
    };
  }, []);

  const handleMouseEnter = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setShowShareMenu(true);
  };

  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setShowShareMenu(false);
    }, 200); // adjust delay here
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    };
  }, []);

  // New useEffect to handle URL parameters
  useEffect(() => {
    const serviceParam = getUrlParameter('service');
    const sectionParam = getUrlParameter('section');

    // Map URL service parameter to form service options
    const serviceMap = {
      'gutter-cleaning': 'Gutter Cleaning',
      'roof-cleaning': 'Roof Cleaning',
      'roof-algae-removal': 'Roof Algae Removal'
    };

    if (serviceParam && serviceMap[serviceParam]) {
      setFormData((prev) => ({ ...prev, service: serviceMap[serviceParam] }));
      setEstimateStep(0); // Open estimate modal
    }

    // Scroll to specific section
    if (sectionParam) {
      const sectionRefs = {
        services: servicesRef,
        'service-area': serviceAreaRef,
        contact: contactRef
      };
      const targetRef = sectionRefs[sectionParam];
      if (targetRef && targetRef.current) {
        targetRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  // Naperville 60565 and surrounding zipcodes within 20 miles
  const validZipcodes = {
    '60540': 'Naperville',
    '60563': 'Naperville',
    '60564': 'Naperville',
    '60565': 'Naperville',
    '60566': 'Naperville',
    '60567': 'Naperville',
    '60516': 'Downers Grove',
    '60515': 'Downers Grove',
    '60517': 'Woodridge',
    '60439': 'Lemont',
    '60440': 'Bolingbrook',
    '60490': 'Bolingbrook',
    '60527': 'Burr Ridge',
    '60521': 'Hinsdale',
    '60522': 'Hinsdale',
    '60523': 'Oak Brook',
    '60181': 'Villa Park',
    '60187': 'Wheaton',
    '60189': 'Wheaton',
    '60137': 'Glen Ellyn',
    '60148': 'Lombard',
    '60525': 'La Grange',
    '60526': 'La Grange Park',
    '60558': 'Western Springs',
    '60532': 'Lisle',
    '60561': 'Darien',
    '60559': 'Westmont',
    '60446': 'Romeoville',
    '60447': 'Romeoville'
  };

  const validateZipcode = (zip) => {
    return validZipcodes[zip] || null;
  };

  const validateName = (name) => {
    const parts = name.trim().split(' ');
    return parts.length >= 2 && parts.every(part => part.length > 0);
  };

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const nextStep = () => {
    const newErrors = {};

    if (estimateStep === 0) {
      if (!formData.service) newErrors.service = 'Please select a service';
    } else if (estimateStep === 1) {
      const townName = validateZipcode(formData.zipcode);
      if (!townName) {
        newErrors.zipcode = 'Sorry, we only service within 20 miles of Naperville, IL 60565';
      }
    } else if (estimateStep === 2) {
      if (!formData.address.trim()) {
        newErrors.address = 'Please enter your street address';
      } else {
        const townName = validateZipcode(formData.zipcode);
        if (!townName) {
          newErrors.zipcode = 'Please verify your zipcode matches your address';
          setEstimateStep(1);
          setErrors(newErrors);
          return;
        }
      }
    } else if (estimateStep === 3) {
      if (!validateName(formData.name)) {
        newErrors.name = 'Please enter both first and last name';
      }
    } else if (estimateStep === 4) {
      if (!validatePhone(formData.phone)) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (estimateStep === 4) {
      setEstimateStep(5); // Move to reminder signup step
    } else if (estimateStep === 5) {
      submitEstimate();
    } else {
      setEstimateStep(prev => prev + 1);
    }
  };

  const submitEstimate = async () => {
    setIsSubmitting(true);
    const townName = validateZipcode(formData.zipcode);

    const formatPhone = (phone) => {
      const cleaned = phone.replace(/\D/g, '');
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    };

    const discordMessage = {
      content: "🏠 **NEW ESTIMATE REQUEST**",
      embeds: [{
        title: "Ceponis Gutters - New Customer Request",
        color: 3447003,
        fields: [
          {
            name: "👤 Customer Name",
            value: formData.name,
            inline: true
          },
          {
            name: "📞 Phone Number",
            value: formatPhone(formData.phone),
            inline: true
          },
          {
            name: "🛠️ Service Requested",
            value: formData.service,
            inline: true
          },
          {
            name: "🏘️ Address",
            value: `${formData.address}\n${townName}, IL ${formData.zipcode}`,
            inline: false
          },
          {
            name: "📅 Reminders",
            value: formData.reminders ? "✅ Opted in for seasonal reminders" : "➖ No reminders requested",
            inline: false
          },
          {
            name: "⏰ Response Required",
            value: "Contact customer within 24 hours via text message",
            inline: false
          }
        ],
        timestamp: new Date().toISOString(),
        footer: {
          text: "Ceponis Gutters Estimate System"
        }
      }]
    };

    try {
      // Replace with your Discord webhook URL
      const DISCORD_WEBHOOK_URL = 'https://discord.com/api/webhooks/1377784675455471667/EX1bV4Y25LE-DzUYTS0GboozkofXMsMDxOwEewuXGcsv410vdzhp8guaNqB_XQP7rCTz';

      const response = await fetch(DISCORD_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(discordMessage)
      });

      if (response.ok) {
        setIsSubmitted(true);
        setErrors({});
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'There was an error submitting your request. Please try again or call us directly at (630) 780-8936.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProgressPercentage = () => {
    return ((estimateStep + 1) / 6) * 100;
  };

  const handleCallClick = () => {
    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      window.location.href = 'sms:6307808936';
    } else {
      window.location.href = 'tel:6307808936';
    }
  };

  const handleShare = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent('Check out Ceponis Gutters - Professional gutter and roof cleaning services in Naperville, IL!');

    const shareUrls = {
      email: `mailto:?subject=Ceponis Gutters&body=${text} ${url}`,
      text: `sms:?body=${text} ${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      whatsapp: `https://wa.me/?text=${text} ${url}`,
    };

    if (platform === 'vcard') {
      const vCardContent = `
BEGIN:VCARD
VERSION:3.0
FN:Mantas Ceponis - Gutters
ORG:Ceponis Gutters
TEL;TYPE=WORK,VOICE:(630) 780-8936
ADR;TYPE=WORK:;;Naperville;Naperville;IL;60565;USA
URL:${window.location.href}
NOTE:Professional gutter cleaning, roof cleaning, and algae removal services in Naperville, IL and surrounding areas.
END:VCARD
      `.trim();

      const blob = new Blob([vCardContent], { type: 'text/vcard' });
      const vCardUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = vCardUrl;
      link.download = 'Ceponis_Gutters_Contact.vcf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(vCardUrl);
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'noopener,noreferrer');
    }

    setShowShareMenu(false);
  };

  const services = [
    {
      title: 'Gutter Cleaning',
      desc: 'Complete gutter cleaning and debris removal to protect your home from water damage and foundation issues.',
      icon: <Home className="w-8 h-8 text-blue-600" />
    },
    {
      title: 'Roof Cleaning',
      desc: 'Professional roof cleaning to maintain your home\'s appearance, value, and extend roof lifespan.',
      icon: <Building className="w-8 h-8 text-blue-600" />
    },
    {
      title: 'Roof Algae/Lichen Removal',
      desc: 'Safe and effective removal of algae, moss, lichen, and mildew to restore your roof\'s original appearance.',
      icon: <Droplets className="w-8 h-8 text-blue-600" />
    }
  ];

  const MapWithRadius = () => {
    // Center of Naperville, IL
    const center = [41.7508, -88.1535];
    // 20 miles in meters (1 mile ≈ 1609.34 meters)
    const radius = 32186.9;

    // Key towns from validZipcodes with coordinates
    const serviceAreaTowns = [
      { name: 'Naperville', zip: '60565', lat: 41.7508, lng: -88.1535 },
      { name: 'Downers Grove', zip: '60515', lat: 41.8028, lng: -88.0112 },
      { name: 'Wheaton', zip: '60189', lat: 41.8661, lng: -88.1070 },
      { name: 'Bolingbrook', zip: '60440', lat: 41.6986, lng: -88.0684 },
      { name: 'Lisle', zip: '60532', lat: 41.8011, lng: -88.0748 },
    ];

    return (
      <MapContainer
        center={center}
        zoom={9} // Adjusted to show ~20-mile radius
        style={{ height: '400px', width: '100%', zIndex: 10 }} // Explicit z-index
        className="rounded-lg"
        dragging={false} // Disable dragging
        touchZoom={false} // Disable touch zoom
        doubleClickZoom={false} // Disable double-click zoom
        scrollWheelZoom={false} // Disable scroll wheel zoom
        boxZoom={false} // Disable box zoom
        keyboard={false} // Disable keyboard navigation
        zoomControl={false} // Hide zoom controls
        attributionControl={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <Circle
          center={center}
          radius={radius}
          pathOptions={{
            color: '#3B82F6', // Blue to match your theme
            fillColor: '#3B82F6',
            fillOpacity: 0.15,
            weight: 2,
          }}
        />
      </MapContainer>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="bg-white bg-opacity-90 backdrop-blur-sm shadow-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-slate-700 via-blue-600 to-slate-600 bg-clip-text text-transparent">
              Ceponis Gutters
            </h1>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-4">
              <a href="/#services" className="text-slate-600 hover:text-blue-600 transition-colors">
                Services
              </a>
              <a href="/?section=contact" className="text-slate-600 hover:text-blue-600 transition-colors">
                Contact Us
              </a>
              <a
                href="/?service=gutter-cleaning"
                className="bg-gradient-to-r from-blue-600 to-slate-700 hover:from-blue-700 hover:to-slate-800 text-white px-6 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                aria-label="Get a free estimate for gutter or roof cleaning"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Get Free Estimate</span>
                <span className="sm:hidden">Estimate</span>
              </a>
              <div
                className="relative"
                ref={shareMenuRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => setShowShareMenu((prev) => !prev)}
                  className="bg-slate-600 hover:bg-slate-700 text-white p-2 rounded-lg transition-colors"
                  aria-label="Share website"
                >
                  <Share2 className="w-5 h-5" />
                </button>

                {showShareMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    <button onClick={() => handleShare('email')} className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center space-x-2">
                      <Mail className="w-4 h-4" /><span>Email</span>
                    </button>
                    <button onClick={() => handleShare('text')} className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center space-x-2">
                      <MessageSquare className="w-4 h-4" /><span>Text Message</span>
                    </button>
                    <button onClick={() => handleShare('facebook')} className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center space-x-2">
                      <Facebook className="w-4 h-4" /><span>Facebook</span>
                    </button>
                    <button onClick={() => handleShare('whatsapp')} className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center space-x-2">
                      <Send className="w-4 h-4" /><span>WhatsApp</span>
                    </button>
                    <button onClick={() => handleShare('vcard')} className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center space-x-2">
                      <User className="w-4 h-4" /><span>Add to Contacts</span>
                    </button>
                  </div>
                )}
              </div>
            </nav>

            {/* Mobile Right Actions */}
            <div className="md:hidden flex items-center space-x-2">
              <a
                href="tel:+16307808936"
                className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg transition-colors"
                aria-label="Call Ceponis Gutters"
              >
                <Phone className="w-5 h-5" />
              </a>
              <a
                href="/?service=gutter-cleaning"
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors text-sm font-semibold"
              >
                Estimate
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-700 hover:text-blue-700"
                aria-label="Open menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 space-y-2">
              <a href="/#services" className="block text-slate-700 hover:text-blue-600">
                Services
              </a>
              <a href="/?service=gutter-cleaning" className="block text-slate-700 hover:text-blue-600">
                Gutter Cleaning
              </a>
              <a href="/?section=contact" className="block text-slate-700 hover:text-blue-600">
                Contact Us
              </a>
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="block w-full text-left text-slate-700 hover:text-blue-600"
              >
                Share
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transform scale-100 transition-transform duration-[10s] ease-in-out animate-subtle-zoom"
          style={{
            backgroundImage: `url(/assets/Maple-Leaves.jpg)`,
            filter: 'blur(8px)',
            WebkitFilter: 'blur(8px)',
            zIndex: 0,
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <img src="/assets/Maple-Leaves.jpg" alt="Maple leaves background for Ceponis Gutters hero section" className="sr-only" />
        </div>

        {/* Hero Content */}
        <div className="relative max-w-6xl mx-auto text-center z-10">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in">
            Professional Gutter &<br />
            <span className="bg-gradient-to-r from-blue-600 to-slate-700 bg-clip-text text-transparent">
              Roof Cleaning
            </span>
          </h2>
          <p className="text-xl text-white mb-8 max-w-2xl mx-auto animate-slide-up">
            Serving Naperville and surrounding areas within a 20-mile radius with quality gutter cleaning, roof cleaning, and algae removal services.
          </p>
          <button
            onClick={() => setEstimateStep(0)}
            className="bg-gradient-to-r from-blue-600 to-slate-700 hover:from-blue-700 hover:to-slate-800 text-white px-8 py-4 rounded-lg text-xl font-semibold transition-all transform hover:scale-105 shadow-lg animate-pulse-subtle"
            aria-label="Get a free estimate for gutter or roof cleaning"
          >
            Get Free Estimate
          </button>
        </div>
      </section>

      {/* Services */}
      <section id="services" ref={servicesRef} className="py-16 px-4 bg-white bg-opacity-60">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-slate-800 mb-12">Our Services</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div key={idx} className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <div className="mb-4 px-4">{service.icon}</div>
                <h4 className="text-xl font-semibold text-slate-800 mb-4 px-4">{service.title}</h4>
                <p className="text-slate-600 px-4">{service.desc}</p>
                &nbsp;
                <div className="px-4" >
                  <button
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, service: service.title }));
                      setEstimateStep(0);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                    aria-label={`Get a free quote for ${service.title.toLowerCase()} in Naperville, IL`}
                  >
                    Get Quote
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section with 20-mile radius */}
      <section id="service-area" ref={serviceAreaRef} className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-slate-800 mb-12">Our Service Area</h3>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <LazyLoad height={400} offset={100}>
              <MapWithRadius />
            </LazyLoad>
            <p className="sr-only">
              Map showing a 20-mile radius around Naperville, IL 60565, covering service areas including {Object.values(validZipcodes).join(', ')}.
            </p>
          </div>
          <div className="text-center text-slate-600 mt-4">
            <p className="text-base mb-2">
              We provide professional gutter cleaning, roof cleaning, and algae removal services and surrounding communities.
            </p>
            <p className="flex items-center justify-center mb-2">
              <MapPin className="w-5 h-5 mr-2" />
              Serving Naperville, IL 60565 and all areas within a 20-mile radius.
            </p>
            <p className="text-sm sr-only">
              Including: {Object.values(validZipcodes).join(', ')} ({Object.keys(validZipcodes).join(', ')}).
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white bg-opacity-60">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-slate-800 mb-12">Frequently Asked Questions</h3>
          <div className="space-y-6">
            {[
              {
                q: "How long will the job take to complete?",
                a: "Most gutter cleaning jobs take 1-2 hours depending on the size of your home. Roof cleaning typically takes 2 hours. We'll provide a time estimate with your quote."
              },
              {
                q: "How much will it cost?",
                a: "Pricing varies based on your home's size, service type, and specific needs. Click <a href='/?service=gutter-cleaning' class='text-blue-600 hover:underline'>here</a> for a personalized quote!"
              },
              {
                q: "Why hire Ceponis Gutters?",
                a: "We're local professionals with years of experience, committed to quality work. We use safe, effective methods and guarantee customer satisfaction."
              },
              {
                q: "Do you provide free estimates?",
                a: "Yes! We offer free, no-obligation estimates for all our services. Simply fill out our <a href='/?service=gutter-cleaning' class='text-blue-600 hover:underline'>estimate form</a> or give us a call/text at (630) 780-8936."
              },
              {
                q: "What areas do you serve?",
                a: "We serve Naperville, IL and all surrounding areas within a 20-mile radius, including Downers Grove, Wheaton, Lisle, Bolingbrook, and many more communities."
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                <h4 className="text-lg font-semibold text-slate-800 mb-3 flex items-start">
                  <span className="text-blue-600 mr-2">Q:</span>
                  {faq.q}
                </h4>
                <p className="text-slate-600 ml-6" dangerouslySetInnerHTML={{ __html: faq.a }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact & Review */}
      <section ref={contactRef} className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-slate-800 mb-8">Get In Touch</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleCallClick}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg flex items-center space-x-2 transition-colors"
              aria-label="Call or text Ceponis Gutters at (630) 780-8936"
            >
              <Phone className="w-5 h-5" />
              <span>Call/Text: (630) 780-8936</span>
            </button>
            <a
              href="https://nextdoor.com/pages/ceponis-gutters-naperville-il/?init_source=search&query=ceponis+gutters&referrer=nextdoor"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-slate-600 hover:bg-slate-700 text-white px-8 py-3 rounded-lg flex items-center space-x-2 transition-colors"
              aria-label="Read and leave reviews for Ceponis Gutters on Nextdoor"
            >
              <Star className="w-5 h-5" />
              <span>Read & Leave Reviews</span>
            </a>
          </div>
          <p className="text-center text-slate-600 mt-6 flex items-center justify-center">
            <MapPin className="w-5 h-5 mr-2" />
            Serving Naperville, IL and surrounding areas
          </p>
        </div>
      </section>

      {/* Estimate Modal */}
      {estimateStep >= 0 && !isSubmitted && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-slate-800">Get Your Estimate</h3>
                <button
                  onClick={() => setEstimateStep(-1)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Close modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
              <p className="text-sm text-slate-600">{Math.round(getProgressPercentage())}% Complete</p>
            </div>

            {estimateStep === 0 && (
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <Home className="w-5 h-5 mr-2 text-blue-600" />
                  What service do you need?
                </h4>
                <div className="space-y-3">
                  {['Gutter Cleaning', 'Roof Cleaning', 'Roof Algae/Lichen Removal'].map(service => (
                    <button
                      key={service}
                      onClick={() => handleInputChange('service', service)}
                      className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${formData.service === service
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                        }`}
                    >
                      {service}
                    </button>
                  ))}
                </div>
                {errors.service && (
                  <p className="text-red-600 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.service}
                  </p>
                )}
              </div>
            )}

            {estimateStep === 1 && (
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                  What's your zip code?
                </h4>
                <input
                  type="text"
                  placeholder="Enter zip code"
                  value={formData.zipcode}
                  onChange={(e) => handleInputChange('zipcode', e.target.value)}
                  className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none text-lg"
                  maxLength="5"
                />
                {errors.zipcode && (
                  <p className="text-red-600 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.zipcode}
                  </p>
                )}
                {formData.zipcode && validateZipcode(formData.zipcode) && (
                  <p className="text-green-600 text-sm mt-2 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Service available in {validateZipcode(formData.zipcode)}
                  </p>
                )}
              </div>
            )}

            {estimateStep === 2 && (
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <Home className="w-5 h-5 mr-2 text-blue-600" />
                  What's your street address?
                </h4>
                <p className="text-sm text-slate-600 mb-3 flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  Service area: {validateZipcode(formData.zipcode)}, {formData.zipcode}
                </p>
                <input
                  type="text"
                  placeholder="Enter street address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none text-lg"
                />
                {errors.address && (
                  <p className="text-red-600 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.address}
                  </p>
                )}
              </div>
            )}

            {estimateStep === 3 && (
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <span className="text-2xl mr-2">👤</span>
                  What's your name?
                </h4>
                <input
                  type="text"
                  placeholder="First and Last Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none text-lg"
                />
                {errors.name && (
                  <p className="text-red-600 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.name}
                  </p>
                )}
              </div>
            )}

            {estimateStep === 4 && (
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <Phone className="w-5 h-5 mr-2 text-blue-600" />
                  What's your phone number?
                </h4>
                <input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none text-lg"
                />
                <p className="text-xs text-slate-500 mt-2 flex items-start">
                  <span className="mr-1">⚠️</span>
                  We will contact you by text message with your estimate
                </p>
                {errors.phone && (
                  <p className="text-red-600 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.phone}
                  </p>
                )}
              </div>
            )}

            {estimateStep === 5 && (
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-blue-600" />
                  Stay Connected
                </h4>
                <p className="text-slate-600 mb-4">
                  Would you like to receive seasonal reminders for gutter and roof maintenance?
                </p>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.reminders}
                      onChange={(e) => handleInputChange('reminders', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-slate-100 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-slate-700">
                      Yes, send me seasonal reminders (Spring, Summer, Fall)
                    </span>
                  </label>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Reminder Schedule:</span>
                    <br />• Spring: Gutter cleaning after winter debris
                    <br />• Summer: Roof inspection and cleaning
                    <br />• Fall: Pre-winter gutter maintenance
                  </p>
                </div>
              </div>
            )}

            <button
              onClick={nextStep}
              disabled={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg font-semibold mt-6 transition-colors flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin">📤</span>
                  <span>Sending...</span>
                </>
              ) : estimateStep === 5 ? (
                <>
                  <span>📤</span>
                  <span>Send My Estimate Request</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            {errors.submit && (
              <p className="text-red-600 text-sm mt-2 flex items-center">
                <span className="mr-1">⚠️</span>
                {errors.submit}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Success Modal */}
      {isSubmitted && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full text-center">
            <div className="text-6xl mb-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-4">Estimate Request Sent!</h3>
            <p className="text-slate-600 mb-6">
              Thank you, {formData.name.split(' ')[0]}! Your estimate request for {formData.service.toLowerCase()} at {formData.address} has been sent to our team.
            </p>
            <p className="text-blue-600 font-semibold mb-4 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              We will contact you by text message within the next 24 hours with your estimate.
            </p>
            {formData.reminders && (
              <p className="text-green-600 font-semibold mb-6 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                You're signed up for seasonal reminders!
              </p>
            )}
            <button
              onClick={() => {
                setIsSubmitted(false);
                setEstimateStep(-1);
                setFormData({ name: '', phone: '', zipcode: '', address: '', service: '', reminders: false });
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">Ceponis Gutters</h3>
            <p className="text-slate-300">Professional Gutter & Roof Cleaning Services</p>
            <p className="text-slate-400 mt-2">Serving the Greater Naperville Area</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <h4 className="font-semibold mb-2 flex items-center justify-center">
                <Phone className="w-4 h-4 mr-2" />
                Contact
              </h4>
              <p className="text-slate-300">
                <a href="tel:6307808936" className="hover:underline">(630) 780-8936</a>
              </p>
              <p className="text-slate-400 text-sm">Call or Text</p>
            </div>
            <div className="text-center">
              <h4 className="font-semibold mb-2 flex items-center justify-center">
                <MapPin className="w-4 h-4 mr-2" />
                Service Area
              </h4>
              <p className="text-slate-300">
                <a href="#service-area" className="hover:underline">Naperville, IL</a>
              </p>
              <p className="text-slate-400 text-sm">20-mile radius</p>
            </div>
            <div className="text-center">
              <h4 className="font-semibold mb-2 flex items-center justify-center">
                <Star className="w-4 h-4 mr-2" />
                Services
              </h4>
              <p className="text-slate-300">
                <a href="#services" className="hover:underline">Gutter & Roof Cleaning</a>
              </p>
              <p className="text-slate-400 text-sm">Algae Removal</p>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-8 text-center text-slate-400">
            <p>© 2025 Ceponis Gutters. All rights reserved.</p>
            <p className="text-sm mt-2">
              <a href="#services" className="hover:underline">Professional Service</a> | Serving Naperville, IL and surrounding areas
            </p>
            <div className="mt-4 text-xs space-y-1 max-w-4xl mx-auto">
              <p><strong>Terms of Service:</strong> Services subject to weather conditions and property accessibility. Estimates valid for 30 days.</p>
              <p><strong>Privacy Policy:</strong> We do not share your personal information with third parties. Contact information used solely for service delivery and optional seasonal reminders.</p>
              <p><strong>Communication:</strong> By requesting an estimate, you consent to contact via text message for service coordination.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CeponisGutters;